<template>
  <div
    v-if="!$store.state.site.loading"
    id="main-wrapper"
    :class="$store.state.site.theme"
  >
    <sweet-modal id="sweet-modal"></sweet-modal>
    <toaster id="toaster"></toaster>
    <cardModal id="cardModal"></cardModal>
    <clipboard id="clipboard"></clipboard>
    <!--DEV TOOLS: button when content.devMode; Layout test panel only on IDE hosts -->
    <div
      v-if="$store.state.site.content.devMode" 
      id="dev-tools"
      class="pull-right"
      style="position: fixed; bottom: 25px; right: 10px;z-index:1000;"
    >
      <div
        v-if="showGlobalSchemaLayoutTest"
        id="global-schema-dev-toggle"
        class="hidden-print"
        :style="globalSchemaDevPanelStyle"
      >
        <!-- GLOBAL_SCHEMA_DEV_PREVIEW: remove entire panel when conversion parity is done; see GLOBAL_SCHEMA_DEV_PREVIEW_REMOVAL.md -->
        <div :style="{ fontSize: '12px', fontWeight: '600', marginBottom: '6px' }">
          Layout test: {{ globalSchemaTestStatus.activeLayout }}
        </div>
        <div style="display:flex; gap:6px; flex-wrap:wrap;">
          <button
            type="button"
            :style="globalSchemaDevBtnStyle('legacy')"
            @click.prevent="setGlobalSchemaTestMode('legacy')"
          >
            Legacy
          </button>
          <button
            type="button"
            :style="globalSchemaDevBtnStyle('global')"
            @click.prevent="setGlobalSchemaTestMode('global')"
          >
            Global
          </button>
          <button
            type="button"
            :style="globalSchemaDevBtnStyle('reconvert')"
            @click.prevent="reconvertGlobalSchemaPreview"
          >
            Re-convert
          </button>
        </div>
      </div>
      <button
        type="button"
        class="hidden-print"
        :style="devBtnStyle"
        @mouseenter="devToolsBtnHover = true"
        @mouseleave="devToolsBtnHover = false"
        @click.prevent="devTools()"
      >
        <i :class="devIcon"></i> Dev Tools
      </button>
    </div>
    
    <div
      id="bfapp"
      :class="{ 
        'sidebar-mini': $store.state.site.sidebarMini,
        'sidebar-opened': $store.state.site.sidebarOpen,
      }"
    >
      <!-- SCHEMA-DRIVEN LAYOUT (Global Schema feature) -->
      <div
        v-if="resolvedGlobalSchema"
        id="bf-global-schema-layout"
      >
        <vue-form-generator
          tag="div"
          :legacy="false"
          :schema="resolvedGlobalSchema"
          :model="globalLayoutModel"
          :options="globalSchemaFormOptions"
        >
        </vue-form-generator>
      </div>
        
      <!-- LEGACY HARD-CODED LAYOUT (Backward compatibility) -->
      <template v-else>
        <!--HEADER-->
        <header-bar v-if="$store.getters.showTopNavBar">
          <!--CUSTOM SLOTS-->
          <template
            v-for="(slotObj, index) in slotsArray"
            :slot="slotObj.slot"
          >
            <html-renderer-old
              :key="index" 
              :template="slotObj.html"
              :app="$store.state.site.content.app"  
              :params="$store.state.site.content.params"  
              :model="$store.getters.getFormSchema(0).model || {}"  
            >
            </html-renderer-old>
          </template>
        </header-bar>
          
        <div
          v-if="headerSlot"
          class="header"
        >
          <html-renderer-old
            :key="_uid" 
            :template="headerSlot.html"
            :app="$store.state.site.content.app"  
            :params="$store.state.site.content.params"  
            :model="$store.getters.getFormSchema(0).model || {}"  
          >
          </html-renderer-old>
        </div>
          
        <!--NAVIGATION-->
        <left-nav-bar v-if="$store.getters.showSideNavBar">
          <template
            v-for="(slotObj, index ) in slotsArray"
            :slot="slotObj.slot"
          >
            <html-renderer-old
              :key="index+'a'"
              :template="slotObj.html" 
              :app="$store.state.site.content.app"  
              :params="$store.state.site.content.params"  
              :model="$store.getters.getFormSchema(0).model || {}"  
            >
            </html-renderer-old>
          </template>
        </left-nav-bar>
          
        <!--MAIN FORM-->
        <router-view 
          :key="$route.fullPath"
          :class="$store.state.site.pageTransitionClasses"
        >
          <template
            v-for="(slotObj, index ) in slotsArray"
            :slot="slotObj.slot"
          >
            <html-renderer-old
              :key="index+'b'" 
              :template="slotObj.html"
              :app="$store.state.site.content.app"  
              :params="$store.state.site.content.params"  
              :model="$store.getters.getFormSchema(0).model || {}"  
            >
            </html-renderer-old>
          </template>
        </router-view>
          
        <!--APP FOOTER SLOT-->
        <html-renderer-old
          v-for="(slotObj, index) in _.filter(slotsArray, function(slotObj) { return slotObj.slot == 'appFooter' })" 
          :key="index+'c'" 
          :template="slotObj.html" 
          :app="$store.state.site.content.app" 
          :params="$store.state.site.content.params"
          :model="$store.getters.getFormSchema(0).model || {}"  
        >
        </html-renderer-old>
      </template>
    </div>
  </div>
</template>

<script>

  import { EventBus } from 'Js/eventBus';
  import {
    resolveGlobalLayoutSchema,
    shouldUseGlobalSchemaLayout,
    getGlobalSchemaTestStatus,
    isGlobalSchemaLayoutTestHost
  } from 'Js/globalSchemaLayout.js';

   //Global registrations
  Vue.component('toaster', () =>  import ( /* webpackChunkName: "core2" */ '@/assets/components/common/toaster.vue'));
  Vue.component('cardModal', () =>  import ( /* webpackChunkName: "core2" */ '@/assets/components/common/cardmodal.vue'));
  Vue.component('sweetModal', () =>  import ( /* webpackChunkName: "core2" */ '@/assets/components/common/sweetmodal.vue'));
  Vue.component('loading', () =>  import ( /* webpackChunkName: "core1" */ '@/assets/components/common/loading.vue'));
  Vue.component('clipboard', () =>  import ( /* webpackChunkName: "core1" */ '@/assets/components/common/clipboard.vue'));
  // Vue.component('html-renderer-old', () =>  import ( /* webpackChunkName: "core1" */ 'Mas/htmlRenderer.js'));
  Vue.component('bfcomp', require('@/assets/components/layouts/formGen/custom/bfcomponent.vue').default);
  Vue.component('multiselect' , () => import ( /* webpackChunkName: "vue-multiselect" */ 'vue-multiselect'));
  
  var moment = require('moment');
  var numeral = require('numeral');
  var marked = require('marked');
  Vue.prototype.moment = moment;
  Vue.prototype.numeral = numeral;
  Vue.prototype.marked = marked;
  window.moment = moment
  window.numeral = numeral
    
  // COMPONENTS
  Vue.component('header-bar', require('./components/headerblock.vue').default);
  Vue.component('left-nav-bar', require('./components/leftnavbar.vue').default);
  
  // Global Schema field types (must be registered before app renders)
  Vue.component('fieldRouterView', require('@/assets/components/layouts/formGen/custom/routerView.vue').default);

  Vue.component('html-renderer-old', {
    render(h) {
      if (!this.templateRender) {
        return h('div', 'loading...');
      }
      else {
        return this.templateRender(
          );
      }
    }
    ,
  
    watch: {
      template: {
        immediate: true, // makes the watcher fire on first render, too.
        handler() {
          // console.log('@HTML-OLD RENDER html: ', this.template )
          var res=Vue.compile(typeof this.template === 'string' ? this.template : '');
          this.templateRender=res.render; // staticRenderFns belong into $options, 
          // appearantly
          this.$options.staticRenderFns=[] // clean the cache of static elements
          // this is a cache with the results from the staticRenderFns
          this._staticTrees=[] // Fill it with the new staticRenderFns
          for (var i in res.staticRenderFns) {
            //staticRenderFns.push(res.staticRenderFns[i]);
            this.$options.staticRenderFns.push(res.staticRenderFns[i])
          } // console.log ('clear errors?')
          // this.error = null
        }
      }
    },
  
    methods: {
      action: function(actions) {
        EventBus.$emit('processActions', {
          actions
        })
      },
      
      validate: function(){
        return true
      },
  
      namedAction: function(name, options) {
        EventBus.$emit('processNamedAction', {
          name,
          options
        })
      }
    },
  
    props: ['template', 'value', 'model', 'props', 'formGen','app', 'params', 'row','schema'],
    
    data() {
      return {
        templateRender: null
      }
    },

    computed: {
      // Expose the root Vue instance for legacy DB-injected templates that reference `vueapp.*`
      vueapp() {
        return this.$root;
      },
      // Expose BF utility object for legacy templates
      BF() {
        return window.BF;
      },
      // Expose the global window for legacy templates that call window.*
      window() {
        return window;
      }
    },
  
  }

)


  export default {name: 'app-view',
  methods: {
    setGlobalSchemaTestMode(mode) {
      if (mode === 'global') {
        this.reconvertGlobalSchemaPreview();
        return;
      }
      this.$store.commit('SET_GLOBAL_SCHEMA_TEST_MODE', mode);
    },

    reconvertGlobalSchemaPreview() {
      const result = BF.convertSiteToGlobalSchema(this.$store.state.site);
      BF.applyGlobalSchemaPreview(result.globalSchema);
      console.log('[globalSchema] Re-converted preview.', result.report);
    },

    globalSchemaDevBtnStyle(mode) {
      const activeMode = this.$store.state.site.globalSchemaTestMode;
      const isActive = mode === 'reconvert'
        ? false
        : activeMode === mode;
      return {
        backgroundColor: isActive ? '#0369a1' : '#475569',
        color: '#fff',
        border: 'none',
        borderRadius: '4px',
        padding: '4px 8px',
        fontSize: '12px',
        cursor: 'pointer'
      };
    },

    devTools: function() {
      EventBus.$emit('processActions', {
        action: "showCardModal",
        function: "action.options.model = model;",
        options: {
          "draggablex": true,
          "scrollable": false,
          "height": "100%",
          "idForm": "0170ADE2-7088-1E47-8D6B-3A23707EA6C3",
          "pivotX": 1.0,
          "pivotY": 0.1,
          "resizable": false,
          "type": "modal",
          "width": "50%"
        }
      }
      )
    }
    ,
  }
  ,
  
  computed: {
    useGlobalSchemaLayout() {
      return shouldUseGlobalSchemaLayout(
        this.$store.state.site.content,
        BF.getQueryParam('_forceGlobalSchema'),
        this.$store.state.site.globalSchemaTestMode
      );
    },

    resolvedGlobalSchema() {
      const content = this.$store.state.site.content;
      if (!shouldUseGlobalSchemaLayout(
        content,
        BF.getQueryParam('_forceGlobalSchema'),
        this.$store.state.site.globalSchemaTestMode
      )) {
        return null;
      }

      const globalSchema = _.get(content, 'globalSchema');
      return resolveGlobalLayoutSchema(globalSchema, this.$route);
    },

    globalSchemaFormOptions() {
      return {
        validateAfterChanged: false,
        legacy: false
      };
    },

    globalLayoutModel() {
      const formSchema = this.$store.getters.getFormSchema(0);
      return formSchema && formSchema.model ? formSchema.model : {};
    },

    globalSchemaTestStatus() {
      return getGlobalSchemaTestStatus(
        this.$store.state.site.content,
        BF.getQueryParam('_forceGlobalSchema'),
        this.$store.state.site.globalSchemaTestMode
      );
    },

    globalSchemaDevPanelStyle() {
      return {
        backgroundColor: '#0284c7', // sky-600 — same family as Dev Tools button
        color: '#fff',
        borderRadius: '6px',
        padding: '8px 10px',
        marginBottom: '8px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
        minWidth: '220px'
      };
    },

    // Backward-compatible alias for tests and tooling.
    globalSchema() {
      const gs = _.get(this.$store.state.site.content, 'globalSchema');
      return !_.isEmpty(gs) ? gs : null;
    },
    
    // Legacy computed properties
    slotsArray: function() {
      return this.$store.state.site.content.slots ? this.$store.state.site.content.slots: []
    }
    ,
    headerSlot: function() {
      return _.find(this.slotsArray, {'slot':'header'})
    }
    ,
  
    devIcon: function() {
      return 'icon-wrench'
    }
    ,
  
    // Solid sky-600 at rest; sky-700 on hover
    devBtnStyle: function() {
      const bg = this.devToolsBtnHover
        ? '#0369a1'
        : '#0284c7' // sky-600 solid
      return {
        backgroundColor: bg,
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        padding: '8px 14px',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer',
        boxShadow: this.devToolsBtnHover ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        lineHeight: '1.25',
        transition: 'background-color 0.15s ease, box-shadow 0.15s ease'
      }
    }
    ,

    // Layout test (Legacy/Global/Re-convert): only on IDE preview hosts
    showGlobalSchemaLayoutTest: function() {
      if (!this.$store.state.site.content.devMode) return false;
      const hostname = typeof window !== 'undefined' && window.location && window.location.hostname || '';
      return isGlobalSchemaLayoutTestHost(hostname);
    }


  }
  ,

  components: {
    // VRuntimeTemplate
  }
  ,
  
  data: function() {
    return {
      devToolsBtnHover: false
    }
  },

  mounted() {
    if (
      this.showGlobalSchemaLayoutTest &&
      this.$store.state.site.globalSchemaTestMode === 'global'
    ) {
      this.reconvertGlobalSchemaPreview();
    }
  }
}
</script>

<style>
/* Flatten only the VFG root so schema App Shell keeps its flex box.
   Lives here (not neu pack) so GS works when cssPacks.neu is off. */
#bfapp > .vue-form-generator,
#bfapp #bf-global-schema-layout > .vue-form-generator {
  display: contents;
}
</style>
